import {normalizeColorId, normalizeIconId} from "../data/avatars.js";
import {freshStats, normalizeStats} from "./matchStats.js";
import {isTeamMode} from "./modes.js";
import {normalizeTeam} from "./teams.js";

function finiteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

// Abilities are game rules, so a forged state must not be able to invent one.
const MINION_ABILITIES = new Set([
  "thorns", "discount", "pinger", "bartender", "popper", "brew", "leech",
  "cooler", "melt", "bouncer", "dj", "tower", "cashier", "partyhat", "photographer", "drifter", "captain",
]);

export const MAX_HAND = 6;
// Card ids from the network are capped like every other synced string; a list is capped
// too. Whether the ids name real cards is the dealer's question (deckRules), not ours.
const CARD_ID_LENGTH = 32;

// Samma igen replays this, so only the shape is trusted: the card id is looked up in
// the library when it is replayed, and an id that names nothing replays nothing.
function normalizeLastPlayed(value) {
  if (!value || typeof value !== "object" || typeof value.cardId !== "string") return null;
  return {
    cardId: value.cardId.slice(0, CARD_ID_LENGTH),
    targetId: typeof value.targetId === "string" ? value.targetId : null,
  };
}

function count(value) {
  return Math.max(0, Math.trunc(finiteNumber(value)));
}

function cardIds(list, limit) {
  return list.slice(0, limit).map((id) => String(id).slice(0, CARD_ID_LENGTH));
}

// Minions arrive inside whole game states POSTed by anyone who knows the code, and
// their fields are rendered into the board. Coerce them to their declared types here
// so nothing downstream has to trust the network.
function normalizeMinion(minion) {
  if (!minion || typeof minion !== "object") return null;
  return {
    ...minion,
    id: String(minion.id ?? ""),
    name: String(minion.name ?? ""),
    hp: finiteNumber(minion.hp),
    maxHp: finiteNumber(minion.maxHp),
    ability: MINION_ABILITIES.has(minion.ability) ? minion.ability : null,
    // Who planted it — Snyltgästen credits its sips to them. Anything else is nobody.
    placedBy: typeof minion.placedBy === "string" ? minion.placedBy : null,
    // The card that put it on the bar: its art key, which only reaches the page through
    // artClass, and the play's event id, which the board uses to keep the minion off the
    // bar until that card has slid to it. Decoration, but synced, so capped like the rest.
    image: typeof minion.image === "string" ? minion.image.slice(0, 32) : null,
    playId: typeof minion.playId === "string" ? minion.playId.slice(0, 64) : null,
  };
}

export class Player {
  static create({name, id, deck = [], isBot = false, icon, color, team = 0, customDeck = null, openingHand = []}) {
    return {
      id,
      name: name || "Spelare",
      isBot,
      // Which side of a 2 mot 2 this seat plays for. Party mode never reads it and
      // leaves everyone on team 0, so the field costs one number per player.
      team: normalizeTeam(team),
      // Seeded on the id so a seat that never opened the picker — every bot, every
      // player from before this existed — still looks like someone in particular.
      icon: normalizeIconId(icon, id),
      color: normalizeColorId(color, id),
      sipsTaken: 0,
      maxSips: 30,
      mana: 0,
      maxMana: 0,
      deck,
      hand: [],
      minions: [],
      reflect: false,
      manaPenalty: 0,
      discount: 0,
      fatigue: 0,
      dodge: false,
      hangover: 0,
      hangoverBy: null,
      ...Player.freshEffects(),
      // What this seat did over the match, for the end screen (matchStats.js).
      stats: freshStats(),
      customDeck,
      openingHand,
    };
  }

  static normalize(player) {
    if (!player) return null;
    if (typeof player.name !== "string") player.name = String(player.name ?? "Spelare");
    if (typeof player.sipsTaken !== "number") player.sipsTaken = Math.max(0, 42 - (player.hp || 42));
    if (typeof player.maxSips !== "number") player.maxSips = 30;
    if (typeof player.mana !== "number") player.mana = 0;
    if (typeof player.maxMana !== "number") player.maxMana = 0;
    if (!Array.isArray(player.deck)) player.deck = [];
    if (!Array.isArray(player.hand)) player.hand = [];
    player.minions = Array.isArray(player.minions) ? player.minions.map(normalizeMinion).filter(Boolean) : [];
    if (typeof player.reflect !== "boolean") player.reflect = false;
    // Whether a seat plays itself is a rule, not decoration: anything but a real
    // boolean has to mean "human", or a forged state could hand a phone's turn to the
    // host screen's bot driver.
    if (typeof player.isBot !== "boolean") player.isBot = false;
    // Both are looked up by id when they are painted — the icon to a symbol, the
    // colour to a hex in a style attribute — so an id that is not ours must never
    // survive to the lookup. Allow-listed rather than escaped, like a minion's ability.
    player.icon = normalizeIconId(player.icon, player.id);
    player.color = normalizeColorId(player.color, player.id);
    // Your side decides who is allowed to hit you, so it is a rule and gets the same
    // allow-listing as isBot rather than being taken at face value.
    player.team = normalizeTeam(player.team);
    if (typeof player.manaPenalty !== "number") player.manaPenalty = 0;
    if (typeof player.discount !== "number") player.discount = 0;
    if (typeof player.fatigue !== "number") player.fatigue = 0;
    if (typeof player.dodge !== "boolean") player.dodge = false;
    player.hangover = Math.max(0, Math.trunc(finiteNumber(player.hangover)));
    if (typeof player.hangoverBy !== "string") player.hangoverBy = null;
    // The second wave's one-shot shields and counters. All of them are rules, so a
    // forged state gets the same coercion as the hangover above.
    player.armor = count(player.armor);
    player.drip = count(player.drip);
    if (typeof player.dripBy !== "string") player.dripBy = null;
    player.recentSips = count(player.recentSips);
    player.playedCount = count(player.playedCount);
    player.freeBar = count(player.freeBar);
    for (const flag of ["rebound", "counterDraw", "lifeline", "skipDraw"]) {
      if (typeof player[flag] !== "boolean") player[flag] = false;
    }
    player.lastPlayed = normalizeLastPlayed(player.lastPlayed);
    // The end screen paints these on every device, so a forged count must not get there.
    player.stats = normalizeStats(player.stats);
    // The deck a phone brought from its account. Only its shape is fixed here; the host
    // checks that it is a legal deck when it deals, and deals the standard one if not.
    player.customDeck = Array.isArray(player.customDeck) ? cardIds(player.customDeck, 64) : null;
    player.openingHand = Array.isArray(player.openingHand) ? cardIds(player.openingHand, 8) : [];
    return player;
  }

  static resetForGame(player, deck, drawCard, openingHand = []) {
    Player.normalize(player);
    player.sipsTaken = 0;
    player.maxSips = 30;
    player.maxMana = 1;
    player.mana = 1;
    player.minions = [];
    player.reflect = false;
    player.manaPenalty = 0;
    player.discount = 0;
    player.fatigue = 0;
    player.dodge = false;
    player.hangover = 0;
    player.hangoverBy = null;
    Object.assign(player, Player.freshEffects());
    player.stats = freshStats();
    player.deck = deck;
    player.hand = [...openingHand];
    // Stop when the deck runs dry: a deck shorter than an opening hand can only come
    // from a forged state, and it must not spin the host forever.
    while (player.hand.length < 4 && drawCard(player)) continue;
  }

  // Everything the second wave of cards leaves on a seat, as it is at the start of a
  // match: armor is Glasunderlägget, rebound Tvåvägsspegeln, counterDraw Kontraskålen,
  // lifeline Livlinan, skipDraw Promenaden, drip Långdrinken, freeBar Fri bar.
  // recentSips is what Hämnden pays back, playedCount what Sockerchocken counts and
  // lastPlayed what Samma igen replays.
  static freshEffects() {
    return {
      armor: 0,
      rebound: false,
      counterDraw: false,
      lifeline: false,
      skipDraw: false,
      drip: 0,
      dripBy: null,
      recentSips: 0,
      playedCount: 0,
      freeBar: 0,
      lastPlayed: null,
    };
  }

  static drawCard(player, maxHand = MAX_HAND) {
    Player.normalize(player);
    if (!player || player.hand.length >= maxHand || player.deck.length === 0) return false;
    player.hand.push(player.deck.shift());
    return true;
  }

  // Ficktjuven: one random card from one hand into the other. A full hand cannot take
  // it, and then the victim keeps it.
  static stealCard(from, to, randomInt) {
    if (!from?.hand?.length || !to || to.hand.length >= MAX_HAND) return false;
    const [card] = from.hand.splice(randomInt(0, from.hand.length - 1), 1);
    to.hand.push(card);
    return true;
  }

  // Bytt är bytt. Both hands are already within MAX_HAND, so the swap cannot overfill.
  static swapHands(first, second) {
    [first.hand, second.hand] = [second.hand, first.hand];
  }

  // Slutsålt: one random card out of a hand, gone like a milled one.
  static discardCard(player, randomInt) {
    if (!player?.hand?.length) return null;
    return player.hand.splice(randomInt(0, player.hand.length - 1), 1)[0];
  }

  // Utslagsvasken: the top cards are gone, not drawn — an emptied deck brings fatigue.
  static millCards(player, count) {
    return player.deck.splice(0, Math.max(0, count));
  }

  static isDead(player) {
    return !player || player.sipsTaken >= player.maxSips;
  }
}

export class PlayerManager {
  static normalizeActiveIndex(state) {
    if (!state) return 0;
    if (!Array.isArray(state.players) || state.players.length === 0) {
      state.active = 0;
      return state.active;
    }
    if (Number.isInteger(state.active) && state.active >= 0 && state.active < state.players.length) {
      return state.active;
    }

    const livingIndex = state.players.findIndex((player) => !Player.isDead(player));
    state.active = livingIndex >= 0 ? livingIndex : 0;
    return state.active;
  }

  constructor(getState, localPlayerId) {
    this.getState = getState;
    this.localPlayerId = localPlayerId;
  }

  get state() {
    return this.getState();
  }

  all() {
    return this.state?.players || [];
  }

  setAll(players) {
    if (this.state) this.state.players = players;
  }

  mine() {
    return this.all().find((player) => player.id === this.localPlayerId) || null;
  }

  current() {
    PlayerManager.normalizeActiveIndex(this.state);
    return this.all()[this.state?.active];
  }

  isHostDevice() {
    return Boolean(this.state && this.state.hostId === this.localPlayerId);
  }

  living() {
    return this.all().filter((player) => !Player.isDead(player));
  }

  // The one place the game decides who may be hit. Every path — the target picker,
  // allEnemies cards, turn-end pings, the bot policy and the board's bursts — comes
  // through here, which is why teaming up is this small a change.
  enemiesOf(player) {
    const living = this.all().filter((candidate) => candidate.id !== player.id && !Player.isDead(candidate));
    if (!isTeamMode(this.state)) return living;
    return living.filter((candidate) => normalizeTeam(candidate.team) !== normalizeTeam(player.team));
  }

  without(playerId) {
    this.setAll(this.all().filter((player) => player.id !== playerId));
  }
}
